import * as types from "../../constants";
import { 
    deletePatient, 
    getAllPatientsInvestigation, 
    saveListPatients, 
    updatePatientPersonalData
} from "../../db";
import {
    addPatient as addPatientService,
    updatePersonalDataPatientService,
    getPatients,
    deletePatientService,
    getPatientEvents,
    getPatientByUuid,
    getCrossInvestigationPatients,
} from "../../services";
import {
    getLastSyncEventId,
    setLastSyncEventId,
    getSyncState
} from "../../utils";
import { PAGINATION_CONFIG } from "../../config/pagination";
import { GROUPED_INVESTIGATION_UUID } from "../../constants/grouped";

export function savePatientAction(investigation, patientData) {
  //Añado un uuid por si falla la conexión tener una referencia de este paciente
  //Para también usarlo en el reenvio.
  patientData.uuid = Math.random().toString(36).substring(2); 
  return async (dispatch) => {
    dispatch({ type: types.SAVE_PATIENT_LOADING });

    return addPatientService(investigation.uuid, patientData)
        .then(async (response) => {
            await saveListPatients([response.patient], investigation);
            const updatedListPatients = await getAllPatientsInvestigation(investigation.uuid);
            investigation.patientsPersonalData = updatedListPatients;
            dispatch({
                type: types.SAVE_PATIENT_SUCCESS,
                patient: {...response.patient},
                investigation:investigation
            });
      })
      .catch((error) => {
        if(!error.status && !error.response){
          const offlinePost = patientData;          
          dispatch({
            type: types.SAVE_PATIENT_OFFLINE,
            patient: {...offlinePost},
            investigation:investigation
          });
        }
        else{
          dispatch({ type: types.SAVE_PATIENT_ERROR });
          throw error;
        }
        
       
      });
  };
}

export function deletePatientAction(uuidInvestigation, uuidPatient, reason) {
    return async (dispatch) => {
        // SKIN-376 / SHER-724: purge only once the server has confirmed. Optimistic
        // purge left a 400 (short reason) rendering "patient does not exist" while
        // the server still held the row.
        return deletePatientService(uuidInvestigation, uuidPatient, reason).then(async (response) => {
            dispatch({
                type: types.DELETE_PATIENT,
                payload: { uuidInvestigation, uuidPatient }
            });
            await deletePatient(uuidPatient);
        });
    }
}

export function fetchPatientsAction(uuidInvestigation, silent = false, options = {}) {
    return async (dispatch, getState) => {
        if(!silent){
            dispatch({ type: types.UPDATING_PATIENTS_LOADING });
        } else {
            dispatch({ type: types.SAVE_PATIENT_LOADING });
        }
        
        const state = getState();
        const investigations = state.investigations;
        const investigation = investigations.data.find(inv => inv.uuid === uuidInvestigation);
        if (!investigation) {
            console.error('❌ Investigation not found:', uuidInvestigation);
            return;
        }

        // Event-based sync: full sync is always paginated snapshot (no timestamp-based API)
        const pageSize = options.pageSize || PAGINATION_CONFIG.DEFAULT_PAGE_SIZE;
        const currentCallTimestamp = new Date().getTime().toString();
        let offset = 0;
        let allPatients = [];
        let hasMore = true;
        let lastResponse = null; // Track last response for latestEventId
        
        try {
            console.time('⏱️ Total patient fetch with pagination');
            console.log(`🚀 Starting pagination: ${pageSize} patients per page, max ${PAGINATION_CONFIG.MAX_TOTAL_PATIENTS}`);
            
            // Fetch patients in batches until no more data
            while (hasMore) {
                // Check if investigation is still current (user might have switched)
                const currentState = getState();
                const currentInvestigationUuid = currentState.investigations.currentInvestigation?.uuid;
                
                if (currentInvestigationUuid !== uuidInvestigation) {
                    console.log('🔄 Investigation changed, stopping pagination');
                    break;
                }
                
                const pageNumber = Math.floor(offset / pageSize) + 1;
                console.log(`📦 Fetching page ${pageNumber} (offset: ${offset}, size: ${pageSize})`);
                
                const response = await getPatients(
                    investigation.uuid,
                    0,
                    { limit: pageSize, offset: offset }
                );
                
                lastResponse = response; // Keep track of last response
                
                // Safety check: backend returns hasMore=true but empty array
                if (response.patients.length === 0 && response.pagination?.hasMore) {
                    console.warn('⚠️ Backend returned hasMore=true with no patients, stopping pagination');
                    hasMore = false;
                    break;
                }
                
                // Add patients from this batch
                allPatients = [...allPatients, ...response.patients];
                
                // Save batch to IndexedDB incrementally (but don't update UI until complete)
                if (response.patients.length > 0) {
                    console.time(`💾 Saving ${response.patients.length} patients to IndexedDB`);
                    await saveListPatients(response.patients, investigation);
                    console.timeEnd(`💾 Saving ${response.patients.length} patients to IndexedDB`);
                    
                    // Dispatch progress update for UI
                    const total = response.pagination?.total || 0;
                    const loaded = allPatients.length;
                    const percentage = total > 0 ? Math.round((loaded / total) * 100) : 0;
                    
                    dispatch({
                        type: types.FETCH_PATIENTS_PROGRESS,
                        loaded: loaded,
                        total: total,
                        percentage: percentage
                    });
                    
                    // Enhanced progress logging for large datasets
                    const progressPercent = response.pagination?.total 
                        ? Math.round((allPatients.length / response.pagination.total) * 100)
                        : '?';
                    console.log(`✅ Progress: ${allPatients.length} patients loaded${response.pagination?.total ? ` / ${response.pagination.total}` : ''} (${progressPercent}%) - Loading continues...`);
                }
                
                // Check if more data exists
                hasMore = response.pagination?.hasMore ?? false;
                offset += pageSize;
                
                // Add pause between batches to prevent overwhelming server/browser
                if (hasMore && PAGINATION_CONFIG.PAUSE_BETWEEN_BATCHES_MS > 0) {
                    await new Promise(resolve => setTimeout(resolve, PAGINATION_CONFIG.PAUSE_BETWEEN_BATCHES_MS));
                }
                
                // Safety check: prevent infinite loop
                if (offset > PAGINATION_CONFIG.MAX_TOTAL_PATIENTS) {
                    console.warn(`🛑 Pagination safety limit reached (${PAGINATION_CONFIG.MAX_TOTAL_PATIENTS} patients)`);
                    break;
                }
            }
            
            console.timeEnd('⏱️ Total patient fetch with pagination');
            console.log(`🎉 Pagination complete: ${allPatients.length} total patients loaded`);
            
            // Final update - only update timestamp when ALL data is loaded
            localStorage.setItem("lastUpdatePatients", currentCallTimestamp);
            
            // Get final list from IndexedDB and dispatch SUCCESS only once at the end
            const finalListPatients = await getAllPatientsInvestigation(investigation.uuid);
            investigation.patientsPersonalData = finalListPatients;
            
            // Save latestEventId for this investigation (only on last page) for event-based incremental sync
            if (lastResponse && lastResponse.pagination?.latestEventId !== undefined) {
                setLastSyncEventId(uuidInvestigation, lastResponse.pagination.latestEventId, finalListPatients.length);
            }
            
            // Dispatch success ONLY after all data is loaded (loading indicator will hide now)
            dispatch({
                type: types.FETCH_NEW_PATIENTS_SUCCESS,
                patients: allPatients,
                investigation: investigation
            });
            
        } catch (error) {
            console.error('❌ Error during paginated fetch:', error);
            
            if (!error.status && !error.response) {
                // Offline mode - don't update timestamp, keep old one for retry
                dispatch({
                    type: types.FETCH_NEW_PATIENTS_SUCCESS,
                    investigation:{investigation:{investigation:{patientsPersonalData:[]}}},
                    patients: [],
                });
            } else {
                // Real error - don't update timestamp
                dispatch({ type: types.SAVE_PATIENT_ERROR });
                throw error;
            }
        }
    };
}

// Alias for backward compatibility - now uses the same implementation as fetchPatientsAction
export const updatePatientsFromId = fetchPatientsAction;

/**
 * Fetches the aggregated patient list across every investigation where the researcher
 * holds CROSS_INVESTIGATION_VIEW. The API returns patients grouped by investigation
 * (each investigation has its own AES key); each group is decrypted with its real
 * investigation object and the merged list is stored under the synthetic `__ALL__` key.
 */
export function fetchCrossInvestigationPatientsAction() {
    return async (dispatch, getState) => {
        dispatch({ type: types.UPDATING_PATIENTS_LOADING });
        try {
            const response = await getCrossInvestigationPatients();
            const groups = response.investigations || [];
            const realInvestigations = getState().investigations.data || [];
            let merged = [];
            const groupBuckets = [];

            for (const group of groups) {
                const realInvestigation = realInvestigations.find(inv => inv.uuid === group.uuid);
                if (!realInvestigation) {
                    console.warn('⚠️ Cross-investigation group has no matching investigation in state:', group.uuid);
                    continue;
                }
                // Decrypt with the real investigation's key and persist to IndexedDB
                // under its real uuid (saveListPatients stamps `uuidInvestigation`).
                await saveListPatients(group.patients, realInvestigation);
                const decrypted = await getAllPatientsInvestigation(group.uuid);
                groupBuckets.push({ uuid: group.uuid, patients: decrypted });
                // `investigationName` inside personalData feeds the grouped-list column.
                merged = merged.concat(decrypted.map(patient => ({
                    ...patient,
                    investigationName: group.name,
                    personalData: patient.personalData
                        ? { ...patient.personalData, investigationName: group.name }
                        : patient.personalData
                })));
            }

            // Dispatch the synthetic bucket FIRST: FETCH_NEW_PATIENTS_SUCCESS clears the
            // loading flag, so `data["__ALL__"]` must already be set when the grouped
            // list re-renders, otherwise it renders with an undefined patient list.
            dispatch({
                type: types.FETCH_NEW_PATIENTS_SUCCESS,
                patients: merged,
                investigation: { uuid: GROUPED_INVESTIGATION_UUID, patientsPersonalData: merged }
            });
            // Then per-investigation buckets: drilling into a patient switches
            // currentInvestigation to its real one, and the detail reads data[realUuid].
            for (const bucket of groupBuckets) {
                dispatch({
                    type: types.FETCH_NEW_PATIENTS_SUCCESS,
                    patients: bucket.patients,
                    investigation: { uuid: bucket.uuid, patientsPersonalData: bucket.patients }
                });
            }
        } catch (error) {
            console.error('❌ Error fetching cross-investigation patients:', error);
            dispatch({ type: types.SAVE_PATIENT_ERROR });
            throw error;
        }
    };
}

// ============================================
// Event-Based Incremental Sync
// ============================================

/**
 * Helper function to process individual patient events
 * @param {Object} event - Patient event object (CREATED, UPDATED, or DELETED)
 * @param {Object} investigation - Investigation object with decryption keys
 */
async function processPatientEvent(event, investigation) {
    try {
        switch (event.eventType) {
            case 'CREATED':
            case 'UPDATED':
                // Fetch latest patient data from backend
                const patientResponse = await getPatientByUuid(
                    investigation.uuid, 
                    event.patientUuid
                );
                
                if (patientResponse.patient) {
                    // Save to IndexedDB (will decrypt automatically)
                    await saveListPatients([patientResponse.patient], investigation);
                    console.log(`✅ ${event.eventType}: ${event.patientUuid}`);
                } else {
                    console.warn(`⚠️ Patient not found for ${event.eventType}: ${event.patientUuid}`);
                }
                break;
                
            case 'DELETED':
                // Remove from IndexedDB
                await deletePatient(event.patientUuid);
                console.log(`🗑️ DELETED: ${event.patientUuid}`);
                break;
                
            default:
                console.warn(`⚠️ Unknown event type: ${event.eventType}`);
        }
    } catch (error) {
        console.error(`❌ Error processing event ${event.eventId ?? event.patientUuid}:`, error);
        // Don't throw - continue processing other events
    }
}

/**
 * Incremental patient sync using event-based approach
 * Fetches only the changes since the last sync (much faster than full sync)
 * 
 * @param {string} uuidInvestigation - Investigation UUID
 * @param {boolean} silent - If true, don't show loading indicators
 * @returns {Function} Redux thunk
 */
export function incrementalSyncPatientsAction(uuidInvestigation, silent = true) {
    return async (dispatch, getState) => {
        console.log('🔄 Starting incremental patient sync (event-based)');
        
        if (!silent) {
            dispatch({ type: types.UPDATING_PATIENTS_LOADING });
        }
        
        const state = getState();
        const investigations = state.investigations;
        const investigation = investigations.data.find(
            inv => inv.uuid === uuidInvestigation
        );
        
        if (!investigation) {
            console.error('❌ Investigation not found:', uuidInvestigation);
            return;
        }
        
        // Check if offline
        if (!navigator.onLine) {
            console.log('📴 Offline mode - skipping incremental sync');
            if (!silent) {
                dispatch({
                    type: types.FETCH_NEW_PATIENTS_SUCCESS,
                    patients: await getAllPatientsInvestigation(uuidInvestigation),
                    investigation: investigation
                });
            }
            return;
        }
        
        try {
            // Get last synced event ID for this investigation (per-investigation sync state)
            let sinceEventId = getLastSyncEventId(uuidInvestigation);
            if (sinceEventId === null) {
                console.log('⚠️ No lastEventId for this investigation, falling back to full sync');
                return dispatch(fetchPatientsAction(uuidInvestigation, silent));
            }

            // Guard: if IndexedDB is empty but sync state exists, force full re-sync.
            // This handles stale localStorage (e.g. from a failed prior sync, or a different browser context).
            const idbPatients = await getAllPatientsInvestigation(uuidInvestigation);
            if (idbPatients.length === 0) {
                console.log('⚠️ IndexedDB is empty but sync state exists — forcing full re-sync');
                return dispatch(fetchPatientsAction(uuidInvestigation, silent));
            }

            console.log(`📊 Last synced event ID [${uuidInvestigation}]: ${sinceEventId}`);
            
            let totalEventsProcessed = 0;
            const EVENT_PAGE_LIMIT = 500; // Backend default, max 1000
            
            // Fetch and process all event pages (backend may return hasMore for large catch-up)
            while (true) {
                const response = await getPatientEvents(uuidInvestigation, sinceEventId, EVENT_PAGE_LIMIT);
                const events = response.events || [];
                
                for (const event of events) {
                    await processPatientEvent(event, investigation);
                    sinceEventId = event.eventId;
                    totalEventsProcessed += 1;
                }
                
                // Persist nextEventId so we can resume if interrupted
                const nextEventId = response.pagination?.nextEventId != null
                    ? response.pagination.nextEventId
                    : (events.length > 0 ? events[events.length - 1].eventId : sinceEventId);
                setLastSyncEventId(uuidInvestigation, nextEventId);
                
                if (!response.pagination?.hasMore) break;
                sinceEventId = nextEventId;
            }
            
            if (totalEventsProcessed === 0) {
                console.log('✅ No new events - database is up to date');
            } else {
                console.log(`✅ Incremental sync complete: ${totalEventsProcessed} events processed, eventId now at ${sinceEventId}`);
            }
            
            const currentPatientCount = await getAllPatientsInvestigation(uuidInvestigation).then(p => p.length);
            setLastSyncEventId(uuidInvestigation, sinceEventId, currentPatientCount);
            
            const updatedPatients = await getAllPatientsInvestigation(uuidInvestigation);
            investigation.patientsPersonalData = updatedPatients;
            
            dispatch({
                type: types.FETCH_NEW_PATIENTS_SUCCESS,
                patients: updatedPatients,
                investigation: investigation
            });
            
        } catch (error) {
            console.error('❌ Incremental sync failed:', error);
            
            // Handle specific error cases
            if (error.status === 401) {
                // Token expired - user will be logged out automatically
                console.log('🔒 Token expired during sync');
                return;
            }
            
            if (error.status >= 500 || !error.response) {
                // Server error or network error - skip this cycle, will retry next time
                console.error('❌ Server/network error, will retry next cycle');
                return;
            }
            
            // For other errors, dispatch error state
            if (!silent) {
                dispatch({ type: types.SAVE_PATIENT_ERROR });
            }
        }
    };
}

export function updatePatientAction(investigation, uuidPatient, patientData) {
    return async (dispatch) => {
      dispatch({ type: types.SAVE_PATIENT_LOADING });
  
      return updatePersonalDataPatientService(investigation.uuid, uuidPatient, patientData)
        .then(async (response) => {
            await saveListPatients([response.patient], investigation);
            const updatedListPatients = await getAllPatientsInvestigation(investigation.uuid);
            investigation.patientsPersonalData = updatedListPatients;
            dispatch({
                type: types.UPDATE_PATIENT_SUCCESS,
                patient: {...response.patient},
                uuidPatient:uuidPatient,
                investigation:investigation
            });
        })
        .catch((error) => {
          if(!error.status && !error.response){
            const offlinePost = patientData;  
            offlinePost.uuid = uuidPatient;
            dispatch({
                type: types.UPDATE_PATIENT_OFFLINE,
                patient: {...offlinePost},
                uuidPatient:uuidPatient,
                investigation:investigation
            });
          }
          else{
            dispatch({ type: types.SAVE_PATIENT_ERROR });
            throw error;
          }
          
          
        });
    };
  }

  export function updatePatientInsuranceAction(uuidInvestigation, uuidPatient, newInsuranceId) {
    return async (dispatch) => {
      // Update the database
      await updatePatientPersonalData(uuidPatient, {coverage: newInsuranceId });
      
      // Update Redux state
      dispatch({
        type: types.UPDATE_PATIENT_INSURANCE_SUCCESS,
        uuidInvestigation,
        uuidPatient,
        newInsuranceId
      });
    };
  }

  // Persist the last patient search so its results can be restored when the user
  // navigates back from a patient detail to the search screen.
  export function setPatientSearchAction(search) {
    return { type: types.SET_PATIENT_SEARCH, search };
  }

  export function clearPatientSearchAction() {
    return { type: types.CLEAR_PATIENT_SEARCH };
  }

